import type {
  ActivityEvent,
  ApiErrorShape,
  BotMode,
  ConnectKrakenRequest,
  ConnectKrakenResponse,
  DashboardSnapshot,
  SessionInfo,
  SetupStatus,
  StrategySettings,
  TradeLedgerItem,
} from "../../shared/contracts";

export interface ActivitySnapshot {
  trades: TradeLedgerItem[];
  events: ActivityEvent[];
}

export class ApiError extends Error {
  readonly code: string;
  readonly details: string[];
  readonly status: number;

  constructor(message: string, code: string, status: number, details: string[] = []) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  csrfToken?: string;
};

let unauthorizedHandler: (() => void) | null = null;
let unauthorizedHandled = false;

export function setUnauthorizedHandler(handler: (() => void) | null) {
  unauthorizedHandler = handler;
  unauthorizedHandled = false;
  return () => {
    if (unauthorizedHandler === handler) unauthorizedHandler = null;
  };
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");
  if (options.body !== undefined) headers.set("Content-Type", "application/json");
  if (options.csrfToken) headers.set("X-CSRF-Token", options.csrfToken);

  const response = await fetch(path, {
    ...options,
    credentials: "same-origin",
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  if (!response.ok) {
    if (response.status === 401 && unauthorizedHandler && !unauthorizedHandled) {
      unauthorizedHandled = true;
      unauthorizedHandler();
    }
    let payload: ApiErrorShape | undefined;
    try {
      payload = (await response.json()) as ApiErrorShape;
    } catch {
      // The worker may return a non-JSON error for an upstream outage.
    }
    throw new ApiError(
      payload?.error ?? "The request could not be completed.",
      payload?.code ?? "REQUEST_FAILED",
      response.status,
      payload?.details,
    );
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  setupStatus: () => request<SetupStatus>("/api/setup/status"),
  session: () => request<SessionInfo>("/api/session"),
  setup: (bootstrapCode: string, password: string) =>
    request<SessionInfo>("/api/setup", {
      method: "POST",
      body: { bootstrapCode, password },
    }),
  login: (password: string) =>
    request<SessionInfo>("/api/login", { method: "POST", body: { password } }),
  logout: (csrfToken?: string) =>
    request<void>("/api/logout", { method: "POST", csrfToken }),
  dashboard: (signal?: AbortSignal) => request<DashboardSnapshot>("/api/dashboard", { signal }),
  activity: () => request<ActivitySnapshot>("/api/activity"),
  botControl: (
    action: "start" | "pause" | "resume" | "stop",
    csrfToken: string,
  ) => request<DashboardSnapshot>("/api/bot/control", { method: "POST", csrfToken, body: { action } }),
  setMode: (mode: BotMode, csrfToken: string, acknowledgeRisk?: boolean) =>
    request<DashboardSnapshot>("/api/bot/mode", {
      method: "POST",
      csrfToken,
      body: { mode, acknowledgeRisk },
    }),
  updateStrategy: (settings: StrategySettings, csrfToken: string) =>
    request<StrategySettings>("/api/strategy", {
      method: "PUT",
      csrfToken,
      body: settings,
    }),
  connectKraken: (credentials: ConnectKrakenRequest, csrfToken: string) =>
    request<ConnectKrakenResponse>("/api/kraken/connect", {
      method: "POST",
      csrfToken,
      body: credentials,
    }),
  testKraken: (csrfToken: string) =>
    request<ConnectKrakenResponse>("/api/kraken/test", { method: "POST", csrfToken }),
  emergency: (csrfToken: string) =>
    request<DashboardSnapshot>("/api/emergency", {
      method: "POST",
      csrfToken,
      body: { confirmation: "CLOSE AND STOP" },
    }),
};

export function getErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : "Something went wrong. Please try again.";
}
